import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Shield, Users, LogOut, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function TeamSelect() {
  const { user, memberships, membershipsLoaded, switchTeam, logout } = useAuth();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(null);

  useEffect(() => {
    // Wenn nur 1 Team verfügbar und bereits aktiv → direkt weiter zum Dashboard
    if (membershipsLoaded && user && memberships.length === 1 && user.active_team_id) {
      navigate("/", { replace: true });
    }
  }, [membershipsLoaded, user, memberships, navigate]);

  const choose = async (teamId) => {
    setSwitching(teamId);
    const res = await switchTeam(teamId);
    setSwitching(null);
    if (res.ok) {
      toast.success(`Mannschaft gewechselt: ${res.user.active_team_name}`);
      navigate("/", { replace: true });
    } else {
      toast.error(res.error || "Wechsel fehlgeschlagen");
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.assign("/");
  };

  if (!membershipsLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-950">
        <div className="font-display text-zinc-500 tracking-wider uppercase text-sm">Lädt…</div>
      </div>
    );
  }

  // Nach Club gruppieren
  const byClub = memberships.reduce((acc, m) => {
    (acc[m.club_id] ||= { name: m.club_name, teams: [] }).teams.push(m);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col" data-testid="team-select-page">
      <header className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 bg-brand flex items-center justify-center rounded-md">
            <Shield className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-lg font-bold tracking-tight">Trainer Profil</div>
            <div className="text-[11px] tracking-wide text-zinc-500">Mannschaft auswählen</div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="text-zinc-400 hover:text-brand-lighter hover:bg-zinc-900 uppercase tracking-wider text-xs"
          data-testid="ts-logout-btn"
        >
          <LogOut className="h-4 w-4 mr-2" /> Abmelden
        </Button>
      </header>

      <main className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-2xl">
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-wider text-brand-lighter mb-2">
              Hallo {user?.name || "Trainer"}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase leading-tight">
              Bitte Mannschaft<br />auswählen
            </h1>
            <p className="text-zinc-400 mt-3 text-sm">
              {memberships.length === 0
                ? "Du bist keiner Mannschaft zugeordnet. Wende dich an deinen Vereinsadministrator."
                : `Du hast Zugriff auf ${memberships.length} ${memberships.length === 1 ? "Mannschaft" : "Mannschaften"}.`}
            </p>
          </div>

          {memberships.length === 0 ? (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-6 text-center text-zinc-400 text-sm" data-testid="ts-empty">
              Keine Mannschaft zugewiesen.
            </div>
          ) : (
            <div className="space-y-6" data-testid="ts-clubs">
              {Object.entries(byClub).map(([clubId, { name: clubName, teams }]) => (
                <div key={clubId}>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">{clubName}</div>
                  <div className="space-y-2">
                    {teams.map((m) => (
                      <button
                        key={m.team_id}
                        type="button"
                        onClick={() => choose(m.team_id)}
                        disabled={switching === m.team_id}
                        className="w-full flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900 hover:border-brand-lighter/50 transition px-5 py-4 text-left disabled:opacity-50"
                        data-testid={`ts-team-${m.team_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 bg-brand/20 flex items-center justify-center rounded-md">
                            <Users className="h-5 w-5 text-brand-lighter" />
                          </div>
                          <div>
                            <div className="font-semibold text-zinc-100">{m.team_name}</div>
                            <div className="text-[11px] uppercase tracking-wider text-zinc-500">
                              {m.role === "club_admin"
                                ? "Vereinsadmin"
                                : m.label === "head"
                                  ? "Cheftrainer"
                                  : m.label === "co"
                                    ? "Co-Trainer"
                                    : "Trainer"}
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-zinc-500" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
